import type { Ref } from 'vue'
import * as locales from '../../shared/locales'

/** Rewards copy is downloaded only when the drawer is opened. */
const loaders = import.meta.glob<Record<string, unknown>>('../../locales/rewards/*.json', { import: 'default' })

const fileByCode = new Map(Object.values(locales).map(locale => [locale.code, locale.file]))

/** The locales vue-i18n falls back to from `locale`, as its `fallbackLocale` option lists them. */
function fallbacksOf(fallback: unknown, locale: string): string[] {
  if (typeof fallback === 'string')
    return [fallback]
  if (Array.isArray(fallback))
    return fallback.filter((code): code is string => typeof code === 'string')
  if (fallback && typeof fallback === 'object') {
    const byLocale = fallback as Record<string, unknown>
    return fallbacksOf(byLocale[locale] ?? byLocale.default, locale)
  }
  return []
}

interface RewardsMessagesTarget {
  locale: Ref<string>
  fallbackLocale: Ref<unknown>
  mergeLocaleMessage: (locale: string, messages: Record<string, unknown>) => void
}

// Per i18n instance, not per module: on the server each request renders with its own.
const merged = new WeakMap<object, Set<string>>()

/**
 * Merges the rewards strings for the current locale and the locales it falls back to — what the
 * main bundle does for every other key — into `i18n`. Settles once they are in; a locale already
 * merged costs nothing. Called by the rewards pages' route middleware, so on the server and in the
 * browser alike before anything of theirs renders.
 */
export async function loadRewardsMessages(i18n: RewardsMessagesTarget): Promise<void> {
  const done = merged.get(i18n) ?? new Set<string>()
  merged.set(i18n, done)
  const locale = i18n.locale.value
  const codes = [...new Set([locale, ...fallbacksOf(i18n.fallbackLocale.value, locale)])]
  await Promise.all(codes.map(async (code) => {
    const load = loaders[`../../locales/rewards/${fileByCode.get(code)}`]
    if (done.has(code) || !load)
      return
    i18n.mergeLocaleMessage(code, await load())
    done.add(code)
  }))
}
